import { ReactNode, Suspense, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  CaretDown,
  CaretUp,
  Envelope,
  Globe,
  HashStraight,
  Info,
  Link,
  MapPin,
  PencilSimple,
  Phone,
  Prohibit,
} from "@phosphor-icons/react"
import { PopoverContent, PopoverPortal, PopoverTrigger } from "@radix-ui/react-popover"
import { PotentiallyUnsafeInnerHtml } from "@/components/potentially-unsafe-inner-html"
import { Button } from "@/components/ui/button"
import { ListItem, ListText } from "@/components/ui/list"
import { Popover } from "@/components/ui/popover"
import { generateLocationLink } from "@/lib/google-maps"
import { joinStrings } from "@/lib/joinString"
import { cn } from "@/lib/utils"
import { CareProviderDetailItem } from "@/modules/care-provider/advanced-search/advanced-care-provider-search-item"
import { CareProvider } from "@/modules/care-provider/advanced-search/types/AdvancedCareProvider"
import { CareProviderIcon } from "@/modules/care-provider/components/care-provider-icon"
import { EditCareProviderDialog } from "@/modules/care-provider/components/edit-care-provider/edit-care-provider-dialog"
import { ScheduleDisplay } from "@/modules/care-provider/components/schedule-display"
import { combineCareProviderTitle } from "@/modules/care-provider/utils/combine-care-provider-title"
import { getCareProviderAddress } from "@/modules/care-provider/utils/get-care-provider-address"
import { getCareProviderRelationName } from "@/modules/care-provider/utils/get-care-provider-relation-name"

interface CareProviderPopupProps {
  isOpen: boolean
  children: ReactNode
  onHoverIn: () => void
  onHoverOut: () => void
  careProvider?: CareProvider
  hasMPNReferral?: boolean
}

const CareProviderPopup: React.FC<CareProviderPopupProps> = ({
  careProvider,
  isOpen,
  children,
  hasMPNReferral = false,
  onHoverOut,
  onHoverIn,
}) => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const [isOpenEditCareProviderDialog, setIsOpenEditCareProviderDialog] = useState(false)

  const firstSpecialty = careProvider?.careProviderSpecialtyTranslations?.[0]?.name
  const title = careProvider ? combineCareProviderTitle(careProvider) : ""

  const address = careProvider ? getCareProviderAddress(careProvider) : ""

  const formattedGlobalLocationNumber = careProvider?.globalLocationNumber
    ? t("admin.careProviders.advancedSearch.cardPopup.globalLocationNumber", {
        number: careProvider?.globalLocationNumber,
      })
    : null

  const formattedCreditorCodeRegister = careProvider?.creditorCodeRegister
    ? t("admin.careProviders.advancedSearch.cardPopup.zsrNumber", {
        number: careProvider?.creditorCodeRegister,
      })
    : null

  const numberResults = joinStrings([formattedCreditorCodeRegister, formattedGlobalLocationNumber])
  const languages = careProvider?.officialLanguageLookupTranslations.map((item) => item.isoLanguageName)

  const googleLink: string = generateLocationLink(address)

  const billsViaName = careProvider?.billsVia
    ? joinStrings([t(`careProvider.billsVia`), combineCareProviderTitle(careProvider.billsVia)], " ")
    : ""

  const partOfNetworkCareProviderName = careProvider?.network?.name
    ? joinStrings([t("careProvider.PartOfNetwork"), careProvider.network.name], " ")
    : ""

  const otherRelation = careProvider?.relationType ? getCareProviderRelationName(careProvider, t) : ""
  const relationNames = [billsViaName, partOfNetworkCareProviderName, otherRelation].filter((item) => item !== "")

  return (
    <>
      <Popover open={isOpen}>
        <PopoverTrigger onMouseEnter={onHoverIn} onMouseLeave={onHoverOut} tabIndex={-1}>
          {children}
        </PopoverTrigger>
        <PopoverPortal>
          <PopoverContent
            className="bg-surface z-50 w-auto rounded-lg p-0"
            align="start"
            autoFocus={false}
            forceMount
            hideWhenDetached
            side="bottom"
            alignOffset={-5}
            sideOffset={-30}
            onMouseLeave={onHoverOut}
            onMouseEnter={onHoverIn}
            onOpenAutoFocus={(e: Event) => e.preventDefault()}
            data-testid="care-provider-information-card">
            <div className="flex">
              <div className="border-border bg-surface z-auto grid min-w-120 cursor-pointer divide-y rounded-lg border shadow-md">
                <div className="grid">
                  <div className="grid-cols-1fr-auto grid content-center items-center rounded-lg px-3 py-0 text-left">
                    <div className="grid py-5 pl-3">
                      <div className="grid grid-cols-[2rem_minmax(0,2fr)_minmax(0,auto)] items-center gap-5">
                        <CareProviderIcon groupType={careProvider?.careProviderGroup.groupType} />
                        <div className="grid">
                          <PotentiallyUnsafeInnerHtml className="truncate" value={title} />
                          <div className="flex">
                            <PotentiallyUnsafeInnerHtml
                              className="text-on-surface-subdued truncate text-xs"
                              value={joinStrings([firstSpecialty, careProvider?.groupName], ", ")}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center">
                        <div>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation()
                              setExpanded(!expanded)
                            }}
                            className="flex h-10 w-10 items-center"
                            variant="circle">
                            {expanded ? (
                              <CaretUp className="text-on-surface-subdued" />
                            ) : (
                              <CaretDown className="text-on-surface-subdued" />
                            )}
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <Button
                          className="h-10 w-10"
                          variant="circle"
                          onClick={(e) => {
                            e.stopPropagation()
                            setIsOpenEditCareProviderDialog(true)
                          }}>
                          <PencilSimple size={20} />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="py-2">
                  <div>
                    {hasMPNReferral && (
                      <CareProviderDetailItem
                        className="bg-surface-subdued text-on-surface-subdued mx-2 py-1"
                        icon={<Prohibit />}
                        value={t("admin.careProviders.advancedSearch.cardPopup.onlyMPNAllowed")}
                      />
                    )}
                    {!careProvider?.isTakesNewPatients && (
                      <CareProviderDetailItem
                        className="bg-surface-subdued text-on-surface-subdued mx-2 py-1"
                        icon={<Prohibit />}
                        value={t("admin.careProviders.tag.noNewPatients")}
                      />
                    )}
                    {relationNames.length > 0 ? (
                      <ListItem
                        className="hover:bg-surface-hovered mx-2 rounded-xl px-3 py-1"
                        data-testid="care-provider-relations">
                        <Link className="text-on-surface-subdued" />
                        <div className="text-primary ml-2 min-w-0">
                          {relationNames.map((name) => (
                            <ListText key={name} className="text-primary">
                              {name}
                            </ListText>
                          ))}
                        </div>
                      </ListItem>
                    ) : null}
                    {address && (
                      <a rel="noreferrer" href={googleLink} target="_blank">
                        <CareProviderDetailItem
                          icon={<MapPin />}
                          value={address}
                          className="hover:bg-surface-hovered active:bg-surface-pressed mx-2 rounded-xl py-1"
                        />
                      </a>
                    )}
                    {careProvider?.detailedSchedule && careProvider.detailedSchedule.length > 0 && (
                      <ScheduleDisplay
                        detailedSchedule={careProvider.detailedSchedule}
                        className="mx-2 py-1"
                        isExpanded={expanded}
                      />
                    )}
                    {languages && (
                      <CareProviderDetailItem
                        className={cn("mx-2", expanded ? "mb-2" : "")}
                        icon={<Info />}
                        value={joinStrings(languages)}
                      />
                    )}
                  </div>
                  {expanded && (
                    <>
                      {careProvider?.phoneNumber && (
                        <CareProviderDetailItem
                          icon={<Phone />}
                          value={careProvider.phoneNumber}
                          className="mx-2 mb-2"
                        />
                      )}
                      {careProvider?.email && (
                        <CareProviderDetailItem icon={<Envelope />} value={careProvider.email} className="mx-2 mb-2" />
                      )}
                      {careProvider?.website && (
                        <CareProviderDetailItem icon={<Globe />} value={careProvider.website} className="mx-2 mb-2" />
                      )}
                      {numberResults && (
                        <CareProviderDetailItem icon={<HashStraight />} value={numberResults} className="mx-2" />
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </PopoverContent>
        </PopoverPortal>
      </Popover>
      {isOpenEditCareProviderDialog && (
        <Suspense fallback={null}>
          <EditCareProviderDialog
            careProviderId={careProvider?.id ?? ""}
            onClose={() => setIsOpenEditCareProviderDialog(false)}
          />
        </Suspense>
      )}
    </>
  )
}

export default CareProviderPopup
